import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { format, formatDistanceToNow } from 'date-fns'
import { useSubscription } from '../../hooks/useSubscription'
import {
  DocumentTextIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LinkIcon,
  TrashIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline'

const numberFormatter = new Intl.NumberFormat('en-US')

// Token status helper
const getTokenStatus = (expiresAt, t) => {
  if (!expiresAt) return { label: 'Unknown', color: 'gray', urgent: false }

  const now = new Date()
  const expiry = new Date(expiresAt)
  const daysUntilExpiry = Math.floor((expiry - now) / (1000 * 60 * 60 * 24))

  if (daysUntilExpiry < 0) {
    return {
      label: t('accounts.tokenStatus.expired'),
      color: 'red',
      urgent: true,
      textColor: 'text-red-600',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-200'
    }
  } else if (daysUntilExpiry < 7) {
    return {
      label: t('accounts.tokenStatus.expiresSoon', { days: daysUntilExpiry }),
      color: 'orange',
      urgent: true,
      textColor: 'text-orange-600',
      bgColor: 'bg-orange-100',
      borderColor: 'border-orange-200'
    }
  } else if (daysUntilExpiry < 30) {
    return {
      label: t('accounts.tokenStatus.expiresSoon', { days: daysUntilExpiry }),
      color: 'yellow',
      urgent: false,
      textColor: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      borderColor: 'border-yellow-200'
    }
  } else {
    return {
      label: t('accounts.tokenStatus.expiresSoon', { days: daysUntilExpiry }),
      color: 'green',
      urgent: false,
      textColor: 'text-green-600',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-200'
    }
  }
}

function AccountCard({
  account,
  isSelected,
  onSelect,
  onRefreshToken,
  onDisconnect,
  onDelete,
  onToggleDetails,
  onBrandSettings,
  onManageSubscription,
  onOpenStripePortal,
  showDisconnectedActions = false
}) {
  const { t } = useTranslation('settings')
  const [refreshing, setRefreshing] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [loadingPortal, setLoadingPortal] = useState(false)

  // Load subscription for this specific account
  const { plan, status, hasStripeCustomer, loading: loadingSubscription } = useSubscription(account.id)

  const tokenStatus = getTokenStatus(account.token_expires_at, t)

  // Plan badge styling
  const getPlanBadgeStyle = (planId) => {
    const styles = {
      free: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
      starter: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
      pro: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' }
    }
    return styles[planId] || styles.free
  }

  const planStyle = getPlanBadgeStyle(plan)

  const handleRefreshToken = async (e) => {
    e.stopPropagation()
    setRefreshing(true)
    try {
      await onRefreshToken(account.id)
    } finally {
      setRefreshing(false)
    }
  }

  const handleToggleDetails = (e) => {
    e.stopPropagation()
    setShowDetails(!showDetails)
    onToggleDetails?.(!showDetails)
  }

  const handleBrandSettings = (e) => {
    e.stopPropagation()
    onBrandSettings(account.id)
  }

  const handleManageSubscription = (e) => {
    e.stopPropagation()
    onManageSubscription(account.id)
  }

  const handleOpenStripePortal = async (e) => {
    e.stopPropagation()
    setLoadingPortal(true)
    try {
      await onOpenStripePortal(account.id)
    } finally {
      setLoadingPortal(false)
    }
  }

  const handleDisconnect = (e) => {
    e.stopPropagation()
    onDisconnect(account)
  }

  const handleDelete = (e) => {
    e.stopPropagation()
    onDelete(account)
  }

  const isDisconnected = account.status === 'disconnected' || account.is_disconnected

  return (
    <div
      className={`card border-2 transition-all ${!isDisconnected ? 'cursor-pointer hover:shadow-lg' : 'opacity-75'} ${
        isSelected && !isDisconnected ? 'border-primary-600 bg-primary-50' : 'border-transparent'
      } ${isDisconnected ? 'bg-gray-50' : ''}`}
      onClick={() => !isDisconnected && onSelect(account.id)}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          {account.profile_picture_url ? (
            <img
              src={account.profile_picture_url}
              alt={account.username}
              className={`w-16 h-16 rounded-full object-cover ${isDisconnected ? 'filter grayscale' : ''}`}
            />
          ) : (
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDisconnected ? 'bg-gray-300' : 'bg-gray-200'}`}>
              <span className="text-2xl">👤</span>
            </div>
          )}
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {account.name || account.username}
            </h3>
            <p className="text-sm text-gray-600">@{account.username}</p>
            <div className="flex items-center space-x-4 mt-2">
              <span className="text-xs text-gray-500">
                {t('accounts.account.stats.followers', { count: numberFormatter.format(account.followers_count || 0) })}
              </span>
              <span className="text-xs text-gray-500">
                {t('accounts.account.stats.posts', { count: numberFormatter.format(account.media_count || 0) })}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end space-y-2">
          {isDisconnected && (
            <span className="px-2 py-1 text-xs font-medium bg-gray-600 text-white rounded">
              {t('accounts.account.badge.disconnected')}
            </span>
          )}
          {isSelected && !isDisconnected && (
            <span className="px-2 py-1 text-xs font-medium bg-primary-600 text-white rounded">
              {t('accounts.account.badge.active')}
            </span>
          )}
          {!loadingSubscription && plan && !isDisconnected && (
            <button
              onClick={handleManageSubscription}
              className={`px-2 py-1 text-xs font-medium rounded border ${planStyle.bg} ${planStyle.text} ${planStyle.border} hover:opacity-80 transition-opacity cursor-pointer`}
              title={t('accounts.account.subscription.manage')}
            >
              {plan.toUpperCase()} Plan
            </button>
          )}
          {!isDisconnected && (
            <div className={`px-2 py-1 text-xs font-medium rounded border ${tokenStatus.bgColor} ${tokenStatus.textColor} ${tokenStatus.borderColor}`}>
              {tokenStatus.label}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-col space-y-2">
        {/* Row 1: Primary Actions */}
        {!isDisconnected ? (
          <>
            {/* Active account actions */}
            <div className="flex items-center space-x-2">
              {tokenStatus.urgent && (
                <button
                  onClick={handleRefreshToken}
                  disabled={refreshing}
                  className="flex-1 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                  aria-label={t('accounts.account.button.refresh')}
                >
                  <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                  <span>{refreshing ? t('accounts.account.button.refreshing') : t('accounts.account.button.refresh')}</span>
                </button>
              )}
              <button
                onClick={handleBrandSettings}
                className="flex-1 px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors border border-primary-200 flex items-center justify-center space-x-2"
                aria-label={t('accounts.account.button.settings')}
              >
                <Cog6ToothIcon className="h-4 w-4" />
                <span>{t('accounts.account.button.settings')}</span>
              </button>
            </div>

            {/* Row 2: View Invoices (only if user has Stripe customer account) */}
            {hasStripeCustomer && (
              <button
                onClick={handleOpenStripePortal}
                disabled={loadingPortal}
                className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                aria-label={t('accounts.billing.viewInvoices')}
              >
                <CreditCardIcon className="h-4 w-4" />
                <span>{loadingPortal ? t('accounts.billing.loading') : t('accounts.billing.viewInvoices')}</span>
              </button>
            )}

            {/* Row 3: Disconnect + Delete */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDisconnect}
                className="flex-1 px-3 py-2 text-sm font-medium text-yellow-600 bg-yellow-50 hover:bg-yellow-100 rounded-lg transition-colors border border-yellow-200 flex items-center justify-center space-x-2"
                aria-label={t('accounts.account.button.disconnect')}
              >
                <LinkIcon className="h-4 w-4" />
                <span>{t('accounts.account.button.disconnect')}</span>
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200 flex items-center justify-center space-x-2"
                aria-label={t('accounts.delete.button')}
              >
                <TrashIcon className="h-4 w-4" />
                <span>{t('accounts.delete.button')}</span>
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Disconnected account actions */}
            <div className="flex items-center space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  // Reconnect functionality would go here
                  console.log('Reconnect account:', account.id)
                }}
                className={`${hasStripeCustomer ? 'flex-1' : 'w-full'} px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors border border-primary-200 flex items-center justify-center space-x-2`}
                aria-label={t('accounts.account.button.reconnect')}
              >
                <LinkIcon className="h-4 w-4" />
                <span>{t('accounts.account.button.reconnect')}</span>
              </button>
              {/* Only show View Invoices if user has ever had a subscription (has Stripe customer account) */}
              {hasStripeCustomer && (
                <button
                  onClick={handleOpenStripePortal}
                  disabled={loadingPortal}
                  className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  aria-label={t('accounts.billing.viewInvoices')}
                >
                  <CreditCardIcon className="h-4 w-4" />
                  <span>{loadingPortal ? t('accounts.billing.loading') : t('accounts.billing.viewInvoices')}</span>
                </button>
              )}
            </div>
            <button
              onClick={handleDelete}
              className="w-full px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200 flex items-center justify-center space-x-2"
              aria-label={t('accounts.delete.button')}
            >
              <TrashIcon className="h-4 w-4" />
              <span>{t('accounts.delete.button')}</span>
            </button>
          </>
        )}

        {/* Subscription Management (if not disconnected) */}
        {!loadingSubscription && plan && !isDisconnected && (
          <button
            onClick={handleManageSubscription}
            className={`w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              plan === 'free'
                ? 'text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg'
                : 'text-purple-600 bg-purple-50 hover:bg-purple-100 border border-purple-200'
            }`}
          >
            {plan === 'free' ? '⭐ ' + t('accounts.account.subscription.upgrade') : t('accounts.account.subscription.manage')}
          </button>
        )}

        {/* View Details toggle (at bottom, only for connected accounts) */}
        {!isDisconnected && (
          <button
            onClick={handleToggleDetails}
            className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-center space-x-2"
            aria-label={showDetails ? t('accounts.account.button.details.hide') : t('accounts.account.button.details.show')}
          >
            {showDetails ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
            <span>{showDetails ? t('accounts.account.button.details.hide') : t('accounts.account.button.details.show')}</span>
          </button>
        )}
      </div>

      {showDetails && !isDisconnected && (
        <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
          {account.facebook_page_name && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('accounts.account.details.facebookPage')}</span>
              <span className="font-medium text-gray-900">{account.facebook_page_name}</span>
            </div>
          )}
          {account.last_synced_at && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('accounts.account.details.lastSynced')}</span>
              <span className="font-medium text-gray-900">
                {formatDistanceToNow(new Date(account.last_synced_at), { addSuffix: true })}
              </span>
            </div>
          )}
          {account.token_expires_at && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('accounts.account.details.tokenExpires')}</span>
              <span className="font-medium text-gray-900">
                {format(new Date(account.token_expires_at), 'PPp')}
              </span>
            </div>
          )}
          {account.created_at && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('accounts.account.details.connectedSince')}</span>
              <span className="font-medium text-gray-900">
                {format(new Date(account.created_at), 'PP')}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

AccountCard.propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
    name: PropTypes.string,
    profile_picture_url: PropTypes.string,
    followers_count: PropTypes.number,
    media_count: PropTypes.number,
    token_expires_at: PropTypes.string,
    facebook_page_name: PropTypes.string,
    last_synced_at: PropTypes.string,
    created_at: PropTypes.string,
    status: PropTypes.string,
    is_disconnected: PropTypes.bool
  }).isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onRefreshToken: PropTypes.func.isRequired,
  onDisconnect: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggleDetails: PropTypes.func,
  onBrandSettings: PropTypes.func.isRequired,
  onManageSubscription: PropTypes.func.isRequired,
  onOpenStripePortal: PropTypes.func.isRequired,
  showDisconnectedActions: PropTypes.bool
}

export default AccountCard